import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {Apollo, gql} from 'apollo-angular';
import {ToastrService} from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  map,
  merge,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import {ImageTypeEnum} from 'src/app/shared/graphql/globalTypes';
import {ErrorService} from 'src/app/shared/services/error.service';
import {countPages} from 'src/app/shared/utils/table.utils';
import {
  BuilderOverview_Get_builders_builderOptions_edges_node,
  BuilderOverview_Get_builders,
  BuilderOverview_Get_buildersVariables,
} from './__generated__/BuilderOverview_Get_builders';
import {
  BuilderOverview_Get_builders_total_count,
  BuilderOverview_Get_builders_total_countVariables,
} from './__generated__/BuilderOverview_Get_builders_total_count';

const GET_BUILDERS_QUERY = gql`
query BuilderOverview_Get_builders($offset: Int, $first: Int, $type: ImageTypeEnum, $orderBy: [String], $search: String) {
  builderOptions(offset: $offset, first: $first, type: $type, orderBy: $orderBy, search: $search) {
    totalCount
    edges {
      node {
        id
        title
        type
        created
        builderConfigId
        description
        thumbnail
      }
    }
  }
}`;

const GET_BUILDERS_TOTAL_COUNT_QUERY = gql`
  query BuilderOverview_Get_builders_total_count($type: ImageTypeEnum, $search: String) {
    builderOptions(type: $type, search: $search) {
      totalCount
    }
  }
`;

@Component({
  selector: 'app-builder-overview',
  templateUrl: './builder-overview.component.html',
  styleUrls: ['./builder-overview.component.scss'],
  standalone: false,
})
export class BuilderOverviewComponent implements OnInit, OnDestroy {
  totalCount: number | null = null;
  builderOptions: BuilderOverview_Get_builders_builderOptions_edges_node[] = [];
  loading = true;
  ImageTypeEnum = ImageTypeEnum;

  imageTypeSelection$ = new BehaviorSubject<ImageTypeEnum | null>(null);
  first = new UntypedFormControl(10);
  offset = new UntypedFormControl(1);
  search = new UntypedFormControl(null);

  pageCount = 1;
  pages: number[] = [];

  private subscriptions: Subscription[] = [];

  constructor(
    private apollo: Apollo,
    private errorService: ErrorService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    const currentPaging$ = this.first.valueChanges.pipe(startWith(this.first.value));

    const offset$ = this.offset.valueChanges.pipe(startWith(this.offset.value));

    const search$ = this.search.valueChanges.pipe(startWith(this.search.value), debounceTime(300));

    const totalCount$ = combineLatest([search$, this.imageTypeSelection$]).pipe(
      switchMap(([search, type]) => {
        return this.apollo
          .watchQuery<
            BuilderOverview_Get_builders_total_count,
            BuilderOverview_Get_builders_total_countVariables
          >({
            query: GET_BUILDERS_TOTAL_COUNT_QUERY,
            variables: {
              search,
              type,
            },
          })
          .valueChanges.pipe(
            map(result => result.data?.builderOptions.totalCount ?? 0),
            catchError(error => {
              this.errorService.handleDefault(error);
              this.toastr.warning($localize`An error occurred retrieving builder count data.`);
              return EMPTY;
            }),
          );
      }),
    );

    this.subscriptions.push(
      merge(search$, currentPaging$).subscribe(() => this.offset.setValue(1)),

      totalCount$.subscribe(totalCount => (this.totalCount = totalCount)),

      combineLatest([currentPaging$, offset$, search$, this.imageTypeSelection$])
        .pipe(
          debounceTime(50),
          switchMap(([first, offset, search, type]) =>
            this.apollo
              .watchQuery<BuilderOverview_Get_builders, BuilderOverview_Get_buildersVariables>({
                query: GET_BUILDERS_QUERY,
                variables: {
                  first,
                  offset: first * (offset - 1),
                  type,
                  search,
                  orderBy: ['-created'],
                },
                notifyOnNetworkStatusChange: true,
              })
              .valueChanges.pipe(
                catchError(error => {
                  this.loading = false;
                  this.errorService.handleDefault(error);
                  this.toastr.warning($localize`An error occurred retrieving builder data.`);
                  return EMPTY;
                }),
              ),
          ),
        )
        .subscribe(output => {
          const builderOptions = output.data?.builderOptions;
          this.loading = output.loading;
          this.builderOptions = (builderOptions?.edges ?? []).map(e => e.node);
          this.pageCount = countPages(builderOptions?.totalCount ?? 0, this.first.value);
          this.pages = Array(this.pageCount)
            .fill(0)
            .map((x, i) => i);
        }),
    );
  }

  nextPage(): void {
    this.offset.setValue(this.offset.value + 1);
  }

  previousPage(): void {
    this.offset.setValue(this.offset.value - 1);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
